/**
 * src/api/client.ts
 *
 * Tiny fetch wrapper around the otr-core HTTP API.
 *
 *  - Calls the API on its own origin (see BASE below).
 *  - Sends an x-api-key from localStorage only when one is set (local dev);
 *    behind Teleport the identity header added at the edge is the credential.
 *  - Throws on any non-2xx so TanStack Query's error path runs.
 *  - For binary responses (PNG screenshots), use `requestBinary()`.
 *
 * Storage key is namespaced separately from observe so the two SPAs
 * can hold different keys (e.g. one read-only, one admin).
 */

// Same origin by default: in prod the /v1/* Pages Function forwards calls (with Teleport's
// identity header) to otr-core; in dev the Vite proxy does. Set VITE_OTR_API_URL only to bypass both.
const BASE = (import.meta.env.VITE_OTR_API_URL || '').replace(/\/$/, '');
const TOKEN_KEY = 'otr.real-browsers.api_key';

export function getApiKey(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setApiKey(key: string): void {
  localStorage.setItem(TOKEN_KEY, key);
}

export function clearApiKey(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getApiBase(): string {
  return BASE;
}


const RELOGIN_AT_KEY = 'otr.real-browsers.relogin_at';
const RELOGIN_WINDOW_MS = 60_000;

/** Send the user back through Teleport for this dashboard. If that already happened in the last
 *  minute, the API is refusing a fresh session, so stop at /login instead of looping. */
export function redirectToTeleportLogin(): void {
  if (typeof window === 'undefined' || window.location.pathname.startsWith('/login')) return;
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOGIN_AT_KEY) ?? 0); } catch { /* storage blocked */ }
  if (Date.now() - last < RELOGIN_WINDOW_MS) {
    window.location.assign('/login?reason=api_unauthorized');
    return;
  }
  try { sessionStorage.setItem(RELOGIN_AT_KEY, String(Date.now())); } catch { /* storage blocked */ }
  const path = window.location.pathname + window.location.search;
  window.location.assign(
    `https://access.otrtech.co.uk/web/launch/${window.location.host}?path=${encodeURIComponent(path)}`,
  );
}

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const apiKey = getApiKey();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (apiKey) headers['x-api-key'] = apiKey;

  const url = `${BASE}${path}`;
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'include',
  });

  if (!res.ok) {
    if (res.status === 401) {
      clearApiKey();
      redirectToTeleportLogin();
    }
    let body: unknown;
    try { body = await res.json(); } catch { /* non-json error response */ }
    throw new ApiError(
      `${opts.method ?? 'GET'} ${path} → ${res.status}`,
      res.status,
      body,
    );
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Same as `request()` but returns a Blob — used for /screenshot
 * (image/png) and any other binary endpoint we add.
 */
export async function requestBinary(path: string): Promise<Blob> {
  const apiKey = getApiKey();
  const headers: Record<string, string> = {};
  if (apiKey) headers['x-api-key'] = apiKey;
  const res = await fetch(`${BASE}${path}`, {
    headers,
    credentials: 'include',
  });
  if (!res.ok) {
    throw new ApiError(`GET ${path} → ${res.status}`, res.status);
  }
  return res.blob();
}
